import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TPhonebookMember } from '../../containers/DeviceDetail/Phonebooks/useGetPhonebooksData';
import useBreakpoints from '../../helpers/useBreakpoints';
import DataGrid from '../DataGrid';
import DeviceTypeLabel from '../DeviceType';
import messages from './messages';

interface IPhonebookMembersDataGridProps {
  loading: boolean;
  members?: TPhonebookMember[];
}

const PhonebookMembersDataGrid = ({ loading, members = [] }: IPhonebookMembersDataGridProps) => {
  const { formatMessage } = useIntl();
  const { upMd } = useBreakpoints();

  const columns = useMemo(
    () => [
      {
        disableColumnMenu: true,
        field: 'name',
        flex: 1,
        headerName: formatMessage(messages.PhonebooksMembersColumnsName),
        renderCell: (cell: GridRenderCellParams<TPhonebookMember, TPhonebookMember>) =>
          loading ? (
            <Skeleton animation="wave" height={25} width="80%" />
          ) : (
            <Box fontWeight="bold">{cell.value?.name}</Box>
          ),
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'deviceId',
        flex: 1,
        headerName: formatMessage(messages.PhonebooksMembersColumnsDeviceId),
        renderCell: (cell: GridRenderCellParams<TPhonebookMember, TPhonebookMember>) =>
          loading ? <Skeleton animation="wave" height={25} width="80%" /> : cell.value?.sipNumber,
        sortable: false,
      },
      {
        disableColumnMenu: true,
        field: 'type',
        flex: 1,
        headerName: formatMessage(messages.PhonebooksMembersColumnsType),
        renderCell: (cell: GridRenderCellParams<TPhonebookMember, TPhonebookMember>) => {
          if (loading) {
            return <Skeleton animation="wave" height={25} width="80%" />;
          }
          if (cell.value?.type && cell.value?.deviceType) {
            return <DeviceTypeLabel type={cell.value.type} deviceType={cell.value.deviceType} />;
          }
          return null;
        },
        sortable: false,
      },
    ],
    [loading, upMd, formatMessage]
  );

  const rows = useMemo(
    () =>
      members?.map((member) => ({
        deviceId: member,
        deviceType: member,
        id: member.id,
        isClickable: false,
        isDisabled: !member.active,
        name: member,
        type: member,
      })),
    [members]
  );

  return (
    <DataGrid
      columns={columns}
      columnVisibilityModel={{
        type: upMd,
      }}
      dataTestId="phonebook-members-data-grid"
      disableRowSelectionOnClick
      hideFooterPagination
      loading={loading}
      rows={rows}
    />
  );
};

export default PhonebookMembersDataGrid;
